import { useState } from "react";
import { Loader2, FileText, X, Check, MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useIsMobile } from "@/hooks/use-mobile";

export interface StewGeneratedDoc {
  id: string;
  una_id: string;
  document_name: string;
  document_type: string;
  custom_content: string;
  /** Compiled HTML from the shared template pipeline; preferred for preview. */
  custom_rendered_content?: string | null;
  status: string;
  version: number;
  requires_signatures: boolean;
  registry_display_name: string;
}

interface Props {
  open: boolean;
  doc: StewGeneratedDoc | null;
  onClose: () => void;
  onRequestChanges: (doc: StewGeneratedDoc, note: string) => void;
  onApproved: (
    doc: StewGeneratedDoc,
    requiresSignature: boolean,
  ) => void;
}

export function StewDocumentReview({
  open,
  doc,
  onClose,
  onRequestChanges,
  onApproved,
}: Props) {
  const isMobile = useIsMobile();
  const { toast } = useToast();
  const [approving, setApproving] = useState(false);
  const [showChangeBox, setShowChangeBox] = useState(false);
  const [changeNote, setChangeNote] = useState("");

  const approve = async () => {
    if (!doc) return;
    setApproving(true);
    const userId = (await supabase.auth.getUser()).data.user?.id ?? null;
    // Signature-required docs stay in 'draft' so the existing SignatureCapture /
    // FinalizeWithSignatures flow can pick them up (wizards only flip to
    // 'finalized' after all signatures land). Non-signature docs finalize now.
    const patch: Record<string, any> = doc.requires_signatures
      ? {} // leave status='draft' for SignatureCapture; no approval columns exist yet
      : {
          status: "final",
          finalized_at: new Date().toISOString(),
          finalized_by: userId,
        };

    let error: any = null;
    if (Object.keys(patch).length > 0) {
      const res = await supabase
        .from("generated_documents")
        .update(patch as any)
        .eq("id", doc.id);
      error = res.error;
    }
    setApproving(false);

    if (error) {
      console.error("StewDocumentReview approve failed:", error);
      toast({
        title: "That didn't approve",
        description:
          "Try once more, and if it keeps happening, open a ticket.",
        variant: "destructive",
      });
      return;
    }
    const nextStatus = doc.requires_signatures ? doc.status : "final";
    onApproved({ ...doc, status: nextStatus }, doc.requires_signatures);
  };


  const submitChangeRequest = () => {
    if (!doc) return;
    const note = changeNote.trim();
    if (!note) {
      toast({
        title: "Add a short note",
        description: "Tell Stew what to change.",
      });
      return;
    }
    onRequestChanges(doc, note);
    setChangeNote("");
    setShowChangeBox(false);
  };

  const body = doc ? (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between px-5 py-3 border-b bg-muted/30">
        <div className="flex items-center gap-2 min-w-0">
          <FileText className="h-4 w-4 shrink-0 text-primary" />
          <div className="min-w-0">
            <p className="text-sm font-medium truncate">
              {doc.registry_display_name}
            </p>
            <p className="text-xs text-muted-foreground truncate">
              {doc.document_name} · v{doc.version} · draft
            </p>
          </div>
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          onClick={onClose}
          aria-label="Close review"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>

      <div className="flex-1 overflow-y-auto px-5 md:px-8 py-6 bg-background">
        <div
          className="ecclesiastical-document max-w-none text-foreground"
          dangerouslySetInnerHTML={{
            __html: doc.custom_rendered_content || doc.custom_content || "",
          }}
        />
      </div>

      <div className="border-t bg-background px-5 py-3 space-y-3">
        {showChangeBox ? (
          <div className="space-y-2">
            <Textarea
              value={changeNote}
              onChange={(e) => setChangeNote(e.target.value)}
              placeholder="What should change?"
              rows={3}
              className="resize-none"
              autoFocus
            />
            <div className="flex justify-end gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setShowChangeBox(false);
                  setChangeNote("");
                }}
              >
                Cancel
              </Button>
              <Button size="sm" onClick={submitChangeRequest}>
                Send to Stew
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col sm:flex-row gap-2 sm:justify-end">
            <Button
              variant="outline"
              onClick={() => setShowChangeBox(true)}
              disabled={approving}
              className="gap-1.5"
            >
              <MessageSquare className="h-4 w-4" />
              Request changes
            </Button>
            <Button
              onClick={approve}
              disabled={approving}
              className="gap-1.5"
            >
              {approving ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Check className="h-4 w-4" />
              )}
              Approve
            </Button>
          </div>
        )}
      </div>
    </div>
  ) : null;

  if (isMobile) {
    return (
      <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
        <SheetContent
          side="bottom"
          className="p-0 h-[100dvh] max-h-[100dvh] w-full sm:max-w-full"
        >
          {body}
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent
        side="right"
        className="p-0 w-full sm:max-w-[45vw] sm:w-[45vw]"
      >
        {body}
      </SheetContent>
    </Sheet>
  );
}
